import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { apiFetch, logEvent } from '../api';

function AppealDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [appeal, setAppeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [faxNumber, setFaxNumber] = useState('');
  const [faxSaved, setFaxSaved] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    fetchAppeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAppeal = async () => {
    try {
      const response = await apiFetch(`/get-appeal/${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.appeal) {
          setAppeal(data.appeal);
          setFaxNumber(data.appeal.appeal_fax_number || '');
        }
      }
    } catch (error) {
      console.log('Error fetching appeal:', error);
    }
    setLoading(false);
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await apiFetch(`/update-appeal/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setAppeal({ ...appeal, status: newStatus });
      }
    } catch (error) {
      console.log('Update error:', error);
    }
    setUpdating(false);
  };

  const handleSaveFax = async () => {
    try {
      const response = await apiFetch(`/update-appeal/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appeal_fax_number: faxNumber }),
      });
      if (!response.ok) throw new Error('Save failed');
      setAppeal({ ...appeal, appeal_fax_number: faxNumber });
      setFaxSaved(true);
      setTimeout(() => setFaxSaved(false), 2000);
    } catch (error) {
      alert('Could not save the fax number. Please try again.');
    }
  };

  const handleArchive = async () => {
    const archived = !appeal.archived;
    if (archived) {
      const confirmed = window.confirm(
        'Archive this appeal? You can restore it from the Archived tab in Appeal History.'
      );
      if (!confirmed) return;
    }
    setArchiving(true);
    try {
      const response = await apiFetch(`/update-appeal/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived }),
      });
      if (!response.ok) throw new Error('Update failed');
      setAppeal({ ...appeal, archived });
    } catch (error) {
      alert('Could not update this appeal. Please try again.');
    }
    setArchiving(false);
  };

  const handleCopy = () => {
    const confirmed = window.confirm(
      'I confirm that a licensed healthcare professional has reviewed and validated this appeal text before use.'
    );
    if (!confirmed) return;
    navigator.clipboard.writeText(appeal.appeal_letter);
    logEvent('copy_appeal', appeal.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = async () => {
    await logEvent('logout');
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (!appeal) {
    return <div style={{ padding: '20px' }}>Appeal not found.</div>;
  }

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '2px solid #1a365d',
        paddingBottom: '15px'
      }}>
        <h1 style={{ color: '#1a365d', margin: 0 }}>Clinix AI</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/appeal-history')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#edf2f7',
              color: '#1a365d',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Back to History
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: '#2d3748', margin: 0 }}>
          {appeal.patient_name}
          {appeal.archived && (
            <span style={{
              marginLeft: '12px',
              padding: '2px 10px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 'normal',
              verticalAlign: 'middle',
              backgroundColor: '#e2e8f0',
              color: '#4a5568'
            }}>
              Archived
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          onClick={handleArchive}
          disabled={archiving}
          style={{
            padding: '8px 14px',
            backgroundColor: 'white',
            color: '#4a5568',
            border: '1px solid #cbd5e0',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            opacity: archiving ? 0.6 : 1
          }}
        >
          {archiving ? 'Saving...' : appeal.archived ? 'Restore' : 'Archive'}
        </button>
        <select
          value={appeal.status || 'pending'}
          onChange={(e) => handleStatusChange(e.target.value)}
          disabled={updating}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
        </select>
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#1a365d', marginBottom: '16px' }}>Patient Information</h3>
        <p><strong>Date of Birth:</strong> {appeal.date_of_birth}</p>
        <p><strong>Diagnosis:</strong> {appeal.diagnosis}</p>
        <p><strong>Requested Medication:</strong> {appeal.requested_medication}</p>
        <p style={{ color: '#718096', fontSize: '13px' }}>
          <strong>Date Generated:</strong> {new Date(appeal.created_at).toLocaleString()}
        </p>
      </div>

      <div style={{
        backgroundColor: '#fff5f5',
        border: '1px solid #fed7d7',
        padding: '24px',
        borderRadius: '10px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#c53030', marginBottom: '12px' }}>
          Why It Was Denied — Plain English
        </h3>
        <p style={{ color: '#2d3748', lineHeight: '1.8', whiteSpace: 'pre-wrap', margin: 0 }}>
          {appeal.denial_explanation}
        </p>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <h3 style={{ color: '#1a365d', margin: 0 }}>Generated Appeal Letter</h3>
          <button
            onClick={handleCopy}
            style={{
              padding: '8px 16px',
              backgroundColor: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              minWidth: '70px'
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', color: '#2d3748', marginBottom: '6px' }}>
            Fax appeal to:
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={faxNumber}
              maxLength={30}
              placeholder="Not found in denial letter - enter the plan's appeal fax number"
              onChange={(e) => setFaxNumber(e.target.value)}
              style={{
                flex: 1,
                maxWidth: '360px',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleSaveFax}
              disabled={faxNumber === (appeal.appeal_fax_number || '')}
              style={{
                padding: '8px 14px',
                backgroundColor: '#edf2f7',
                color: '#1a365d',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: faxNumber === (appeal.appeal_fax_number || '') ? 0.5 : 1
              }}
            >
              {faxSaved ? 'Saved' : 'Save'}
            </button>
          </div>
          <p style={{ color: '#718096', fontSize: '12px', margin: '6px 0 0' }}>
            Taken from the denial letter. Always confirm it before faxing.
          </p>
        </div>
        <p style={{ color: '#2d3748', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
          {appeal.appeal_letter}
        </p>
      </div>
    </div>
  );
}

export default AppealDetail;